import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Shield, ArrowLeft, CheckCircle, XCircle, Eye, Clock, FileVideo, Image, HardDrive, DollarSign, Users, BarChart3, Wallet, QrCode, Signal } from 'lucide-react';
import { GuaranteesTab } from '@/components/GuaranteesTab';
import axios from 'axios';

function formatBytes(b) { if (!b) return '0 B'; const k=1024,s=['B','KB','MB','GB']; const i=Math.floor(Math.log(b)/Math.log(k)); return parseFloat((b/Math.pow(k,i)).toFixed(1))+' '+s[i]; }
function formatDate(iso) { if(!iso) return '--'; return new Date(iso).toLocaleDateString('en-US',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'}); }

function StatusBadge({ status }) {
  const map = { pending:'bg-gray-500/10 text-gray-400 border-gray-500/20', approved:'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', rejected:'bg-red-500/10 text-red-500 border-red-500/20', active:'bg-blue-500/10 text-blue-400 border-blue-500/20', processing:'bg-blue-500/10 text-blue-400 border-blue-500/20', paid:'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' };
  return <Badge className={`px-2 py-1 rounded text-xs font-bold uppercase tracking-wider font-mono border ${map[status]||map.pending}`}>{status}</Badge>;
}

export default function AdminPanel() {
  const { token, API } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('ads');
  const [pendingAds, setPendingAds] = useState([]);
  const [allAds, setAllAds] = useState([]);
  const [pendingPlacements, setPendingPlacements] = useState([]);
  const [allPlacements, setAllPlacements] = useState([]);
  const [cpmTiers, setCpmTiers] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [stats, setStats] = useState(null);
  const [creatorApps, setCreatorApps] = useState([]);
  const [reviewAd, setReviewAd] = useState(null);
  const [reviewPlacement, setReviewPlacement] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [previewAd, setPreviewAd] = useState(null);
  const [showTierDialog, setShowTierDialog] = useState(false);
  const [tierForm, setTierForm] = useState({ label:'', ccvMin:'', ccvMax:'', floorCpm:'' });
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    try {
      const [pa, aa, pp, ap, ct, py, st, ca] = await Promise.all([
        axios.get(`${API}/ads/pending`, { headers }),
        axios.get(`${API}/ads/all`, { headers }),
        axios.get(`${API}/placements/pending`, { headers }),
        axios.get(`${API}/placements/all`, { headers }),
        axios.get(`${API}/cpm-tiers`, { headers }),
        axios.get(`${API}/payouts/all`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/creator-applications`, { headers }),
      ]);
      setPendingAds(pa.data); setAllAds(aa.data); setPendingPlacements(pp.data);
      setAllPlacements(ap.data); setCpmTiers(ct.data); setPayouts(py.data); setStats(st.data); setCreatorApps(ca.data);
    } catch (err) { console.error(err); }
  }, [API, token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const approveAd = async (id) => { try { await axios.post(`${API}/ads/${id}/approve`, { notes: reviewNotes }, { headers }); toast.success('Ad approved'); setReviewAd(null); setReviewNotes(''); fetchAll(); } catch(e) { toast.error(e.response?.data?.detail||'Failed'); } };
  const rejectAd = async (id) => { if(!reviewNotes.trim()){toast.error('Notes required');return;} try { await axios.post(`${API}/ads/${id}/reject`, { notes: reviewNotes }, { headers }); toast.success('Ad rejected'); setReviewAd(null); setReviewNotes(''); fetchAll(); } catch(e) { toast.error(e.response?.data?.detail||'Failed'); } };
  const approvePlacement = async (id) => { try { await axios.post(`${API}/placements/${id}/approve`, { notes: reviewNotes }, { headers }); toast.success('Placement approved'); setReviewPlacement(null); setReviewNotes(''); fetchAll(); } catch(e) { toast.error(e.response?.data?.detail||'Failed'); } };
  const rejectPlacement = async (id) => { if(!reviewNotes.trim()){toast.error('Notes required');return;} try { await axios.post(`${API}/placements/${id}/reject`, { notes: reviewNotes }, { headers }); toast.success('Placement rejected'); setReviewPlacement(null); setReviewNotes(''); fetchAll(); } catch(e) { toast.error(e.response?.data?.detail||'Failed'); } };

  const seedTiers = async () => { try { const r = await axios.post(`${API}/cpm-tiers/seed`, {}, { headers }); toast.success(r.data.message); fetchAll(); } catch(e) { toast.error('Failed'); } };
  const createTier = async () => {
    try { await axios.post(`${API}/cpm-tiers`, { label:tierForm.label, ccvMin:parseInt(tierForm.ccvMin), ccvMax:parseInt(tierForm.ccvMax), floorCpm:parseFloat(tierForm.floorCpm) }, { headers }); toast.success('Tier created'); setShowTierDialog(false); setTierForm({label:'',ccvMin:'',ccvMax:'',floorCpm:''}); fetchAll(); } catch(e) { toast.error(e.response?.data?.detail||'Failed'); }
  };
  const deleteTier = async (id) => { try { await axios.delete(`${API}/cpm-tiers/${id}`, { headers }); toast.success('Deleted'); fetchAll(); } catch(e) { toast.error('Failed'); } };
  const processPayout = async (id, action) => { try { await axios.post(`${API}/payouts/${id}/${action}`, {}, { headers }); toast.success(`Payout ${action}d`); fetchAll(); } catch(e) { toast.error('Failed'); } };
  const approveCreator = async (id) => { try { await axios.post(`${API}/admin/creator-applications/${id}/approve`, {}, { headers }); toast.success('Creator approved!'); fetchAll(); } catch(e) { toast.error(e.response?.data?.detail||'Failed'); } };
  const rejectCreator = async (id) => { try { await axios.post(`${API}/admin/creator-applications/${id}/reject`, {}, { headers }); toast.success('Creator rejected'); fetchAll(); } catch(e) { toast.error(e.response?.data?.detail||'Failed'); } };
  const revokeAd = async (id) => { try { const r = await axios.post(`${API}/ads/${id}/revoke`, {}, { headers }); toast.success(r.data.message); fetchAll(); } catch(e) { toast.error(e.response?.data?.detail||'Failed'); } };

  const getFullUrl = (url) => url?.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;

  return (
    <div className="min-h-screen bg-[#050505]">
      <header className="border-b border-white/10 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button data-testid="back-to-home" variant="ghost" onClick={() => navigate('/')} className="text-gray-400 hover:text-white hover:bg-white/5 p-2"><ArrowLeft className="w-4 h-4" /></Button>
            <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-[#FF3B30]" /><span className="font-heading text-lg font-bold text-white uppercase">Admin Panel</span></div>
          </div>
          {stats && <div className="flex gap-2">
            <Badge className="bg-yellow-500/10 text-yellow-400 border-yellow-500/20 font-mono text-xs"><Clock className="w-3 h-3 mr-1" />{stats.pendingAds} ads</Badge>
            <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20 font-mono text-xs"><Users className="w-3 h-3 mr-1" />{stats.pendingPlacements} bids</Badge>
            <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 font-mono text-xs"><Wallet className="w-3 h-3 mr-1" />{stats.pendingPayouts} payouts</Badge>
            {stats.pendingCreatorApps > 0 && <Badge className="bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/20 font-mono text-xs animate-pulse"><Users className="w-3 h-3 mr-1" />{stats.pendingCreatorApps} apps</Badge>}
          </div>}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white/5 border border-white/10 mb-6 flex-wrap">
            <TabsTrigger data-testid="tab-creators" value="creators" className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white font-mono text-xs uppercase tracking-wider">Creators ({creatorApps.length})</TabsTrigger>
            <TabsTrigger data-testid="tab-ads" value="ads" className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white font-mono text-xs uppercase tracking-wider">Ads ({pendingAds.length})</TabsTrigger>
            <TabsTrigger data-testid="tab-placements" value="placements" className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white font-mono text-xs uppercase tracking-wider">Placements ({allPlacements.length})</TabsTrigger>
            <TabsTrigger data-testid="tab-tiers" value="tiers" className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white font-mono text-xs uppercase tracking-wider">CPM Tiers</TabsTrigger>
            <TabsTrigger data-testid="tab-payouts" value="payouts" className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white font-mono text-xs uppercase tracking-wider">Payouts ({payouts.filter(p=>p.status==='pending').length})</TabsTrigger>
            <TabsTrigger data-testid="tab-guarantees" value="guarantees" className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white font-mono text-xs uppercase tracking-wider">Guarantees</TabsTrigger>
            <TabsTrigger data-testid="tab-ccv" value="ccv" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white font-mono text-xs uppercase tracking-wider">Live CCV</TabsTrigger>
            <TabsTrigger data-testid="tab-billing" value="billing" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white font-mono text-xs uppercase tracking-wider">Billing</TabsTrigger>
          </TabsList>

          {/* GUARANTEED RATES TAB */}
          <TabsContent value="guarantees">
            <GuaranteesTab API={API} token={token} />
          </TabsContent>

          {/* CREATOR APPLICATIONS TAB */}
          <TabsContent value="creators">
            <h3 className="font-heading text-lg font-bold text-white mb-2">Creator Applications</h3>
            <p className="text-xs text-gray-500 mb-6">Review and verify creator data before granting platform access.</p>
            {creatorApps.length === 0 ? (
              <div className="text-center py-16 text-gray-500"><CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>No pending applications</p></div>
            ) : (
              <div className="space-y-4">
                {creatorApps.map(app => {
                  const p = app.profile;
                  return (
                    <div key={app.id} data-testid={`creator-app-${app.id}`} className="border border-yellow-500/20 rounded-lg bg-yellow-500/[0.02] p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 rounded-xl bg-[#121212] border border-white/10 flex items-center justify-center overflow-hidden">
                            {p?.profileImageUrl ? <img src={getFullUrl(p.profileImageUrl)} alt="" className="w-full h-full object-cover" /> :
                              <span className="text-2xl font-heading font-black text-gray-600">{(app.name || '?')[0].toUpperCase()}</span>}
                          </div>
                          <div>
                            <h4 className="text-white font-bold text-lg">{app.name}</h4>
                            <p className="text-xs text-gray-500 font-mono">{app.email}</p>
                            <p className="text-[10px] text-gray-600 font-mono">Applied {formatDate(app.createdAt)}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button data-testid={`approve-creator-${app.id}`} onClick={() => approveCreator(app.id)}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm px-5">
                            <CheckCircle className="w-4 h-4 mr-1" /> Approve
                          </Button>
                          <Button data-testid={`reject-creator-${app.id}`} variant="ghost" onClick={() => rejectCreator(app.id)}
                            className="text-red-400 hover:bg-red-500/10 border border-red-500/20 text-sm px-5">
                            <XCircle className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                      {/* Application Data */}
                      {p ? (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="p-3 border border-white/5 rounded bg-white/[0.02]">
                            <p className="text-[9px] font-mono text-gray-600 uppercase">Display Name</p>
                            <p className="text-sm text-white font-medium mt-0.5">{p.displayName || '--'}</p>
                          </div>
                          <div className="p-3 border border-white/5 rounded bg-white/[0.02]">
                            <p className="text-[9px] font-mono text-gray-600 uppercase">Encoder</p>
                            <p className="text-sm text-white font-medium mt-0.5">{p.encoder || '--'}</p>
                          </div>
                          <div className="p-3 border border-white/5 rounded bg-white/[0.02]">
                            <p className="text-[9px] font-mono text-gray-600 uppercase">Avg CCV</p>
                            <p className="text-sm text-white font-mono font-bold mt-0.5">{(p.avgCCV || 0).toLocaleString()}</p>
                          </div>
                          <div className="p-3 border border-white/5 rounded bg-white/[0.02]">
                            <p className="text-[9px] font-mono text-gray-600 uppercase">Monthly Uniques</p>
                            <p className="text-sm text-white font-mono font-bold mt-0.5">{(p.uniqueViewersMonthly || 0).toLocaleString()}</p>
                          </div>
                          <div className="p-3 border border-white/5 rounded bg-white/[0.02]">
                            <p className="text-[9px] font-mono text-gray-600 uppercase">Peak Viewers</p>
                            <p className="text-sm text-white font-mono font-bold mt-0.5">{(p.peakViewers || 0).toLocaleString()}</p>
                          </div>
                          <div className="p-3 border border-white/5 rounded bg-white/[0.02]">
                            <p className="text-[9px] font-mono text-gray-600 uppercase">Platforms</p>
                            <p className="text-sm text-white mt-0.5">{p.platforms?.map(pl => pl.name || pl).join(', ') || '--'}</p>
                          </div>
                          <div className="p-3 border border-white/5 rounded bg-white/[0.02]">
                            <p className="text-[9px] font-mono text-gray-600 uppercase">Categories</p>
                            <p className="text-sm text-white mt-0.5">{p.categories?.join(', ') || '--'}</p>
                          </div>
                          <div className="p-3 border border-white/5 rounded bg-white/[0.02]">
                            <p className="text-[9px] font-mono text-gray-600 uppercase">Schedule</p>
                            <p className="text-sm text-white mt-0.5">{p.schedule || '--'}</p>
                          </div>
                          {p.bio && <div className="col-span-full p-3 border border-white/5 rounded bg-white/[0.02]">
                            <p className="text-[9px] font-mono text-gray-600 uppercase">Bio</p>
                            <p className="text-sm text-gray-300 mt-0.5">{p.bio}</p>
                          </div>}
                          {p.platformAudience && Object.keys(p.platformAudience).length > 0 && (
                            <div className="col-span-full p-3 border border-white/5 rounded bg-white/[0.02]">
                              <p className="text-[9px] font-mono text-gray-600 uppercase mb-2">Platform Audience Breakdown</p>
                              <div className="flex gap-4">
                                {Object.entries(p.platformAudience).map(([plat, count]) => (
                                  <span key={plat} className="text-xs font-mono text-white"><span className="text-gray-500">{plat}:</span> {count.toLocaleString()}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500">No profile data submitted yet — creator registered but didn't complete onboarding.</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* ADS TAB */}
          <TabsContent value="ads">
            {pendingAds.length === 0 ? <div className="text-center py-16 text-gray-500"><CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>No pending ads</p></div> : (
              <div className="border border-white/10 rounded-lg overflow-hidden">
                <Table><TableHeader><TableRow className="border-white/10 bg-white/[0.02]"><TableHead className="text-gray-500 font-mono text-xs uppercase w-16"></TableHead><TableHead className="text-gray-500 font-mono text-xs uppercase">File / Brand</TableHead><TableHead className="text-gray-500 font-mono text-xs uppercase">Type</TableHead><TableHead className="text-gray-500 font-mono text-xs uppercase">Size</TableHead><TableHead className="text-gray-500 font-mono text-xs uppercase">Date</TableHead><TableHead className="text-gray-500 font-mono text-xs uppercase">Actions</TableHead></TableRow></TableHeader>
                <TableBody>{pendingAds.map(ad => {const iv=ad.mimeType?.startsWith('video/');return(
                  <TableRow key={ad.id} className="border-white/5 hover:bg-white/[0.02]">
                    <TableCell><div className="w-12 h-8 bg-black rounded overflow-hidden">{iv?<video src={getFullUrl(ad.url)} className="w-full h-full object-cover" muted preload="metadata"/>:<img src={getFullUrl(ad.url)} alt="" className="w-full h-full object-cover"/>}</div></TableCell>
                    <TableCell><p className="text-sm text-white truncate max-w-[200px]">{ad.filename}</p><p className="text-xs text-gray-500 font-mono">{ad.creatorName}</p></TableCell>
                    <TableCell><span className="text-xs text-gray-400 font-mono">{iv?'Video':'Image'}</span></TableCell>
                    <TableCell><span className="text-xs text-gray-400 font-mono">{formatBytes(ad.fileSize)}</span></TableCell>
                    <TableCell><span className="text-xs text-gray-400 font-mono">{formatDate(ad.createdAt)}</span></TableCell>
                    <TableCell><Button data-testid={`review-ad-${ad.id}`} size="sm" variant="ghost" className="text-gray-400 hover:text-emerald-400 hover:bg-emerald-500/10 h-8 px-2" onClick={()=>{setReviewAd(ad);setReviewNotes('');}}>Review</Button></TableCell>
                  </TableRow>);})}</TableBody></Table>
              </div>
            )}
            )}
            {/* All Ads with Revoke */}
            <h3 className="font-heading text-lg font-bold text-white mt-8 mb-2">All Ads ({allAds.length})</h3>
            <p className="text-xs text-gray-500 mb-4">Revoke approved ads to pull them from circulation.</p>
            <div className="border border-white/10 rounded-lg overflow-hidden">
              <Table><TableHeader><TableRow className="border-white/10 bg-white/[0.02]"><TableHead className="text-gray-500 font-mono text-xs uppercase w-16"></TableHead><TableHead className="text-gray-500 font-mono text-xs uppercase">File / Brand</TableHead><TableHead className="text-gray-500 font-mono text-xs uppercase">Type</TableHead><TableHead className="text-gray-500 font-mono text-xs uppercase">Status</TableHead><TableHead className="text-gray-500 font-mono text-xs uppercase">Actions</TableHead></TableRow></TableHeader>
              <TableBody>{allAds.map(ad => {const iv=ad.mimeType?.startsWith('video/');return(
                <TableRow key={ad.id} className="border-white/5 hover:bg-white/[0.02]">
                  <TableCell><div className="w-12 h-8 bg-black rounded overflow-hidden">{iv?<video src={getFullUrl(ad.url)} className="w-full h-full object-cover" muted preload="metadata"/>:ad.filename?.endsWith('.html')?<div className="w-full h-full bg-emerald-500/10 flex items-center justify-center"><span className="text-[7px] font-mono text-emerald-400">HTML</span></div>:<img src={getFullUrl(ad.url)} alt="" className="w-full h-full object-cover"/>}</div></TableCell>
                  <TableCell><p className="text-sm text-white truncate max-w-[200px]">{ad.filename}</p><p className="text-xs text-gray-500 font-mono">{ad.creatorName}</p></TableCell>
                  <TableCell><span className="text-xs text-gray-400 font-mono">{ad.adType === 'holding' ? 'Holding' : ad.adType === 'overlay' ? 'Overlay' : `${ad.slotDuration||15}s`}</span></TableCell>
                  <TableCell><StatusBadge status={ad.status}/></TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => { setReviewAd(ad); setReviewNotes(''); }} className="text-gray-400 hover:text-white hover:bg-white/5 h-7 text-xs"><Eye className="w-3 h-3 mr-1" />Preview</Button>
                      {ad.status === 'approved' && <Button data-testid={`revoke-ad-${ad.id}`} size="sm" variant="ghost" onClick={() => revokeAd(ad.id)} className="text-red-400 hover:bg-red-500/10 h-7 text-xs">Revoke</Button>}
                    </div>
                  </TableCell>
                </TableRow>);})}</TableBody></Table>
            </div>
          </TabsContent>

          {/* PLACEMENTS TAB (Read-Only Overview) */}
          <TabsContent value="placements">
            <h3 className="font-heading text-lg font-bold text-white mb-2">Marketplace Activity</h3>
            <p className="text-xs text-gray-500 mb-6">Bids go directly to creators. This is a read-only overview of all placement activity.</p>
            {allPlacements.length === 0 ? <div className="text-center py-16 text-gray-500"><CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>No placements yet</p></div> : (
            <div className="border border-white/10 rounded-lg overflow-hidden">
              <Table><TableHeader><TableRow className="border-white/10 bg-white/[0.02]"><TableHead className="text-gray-500 font-mono text-xs uppercase">Brand → Streamer</TableHead><TableHead className="text-gray-500 font-mono text-xs uppercase">Ad</TableHead><TableHead className="text-gray-500 font-mono text-xs uppercase">Bid CPM</TableHead><TableHead className="text-gray-500 font-mono text-xs uppercase">Impressions</TableHead><TableHead className="text-gray-500 font-mono text-xs uppercase">Spend</TableHead><TableHead className="text-gray-500 font-mono text-xs uppercase">Status</TableHead></TableRow></TableHeader>
              <TableBody>{allPlacements.map(p=>(
                <TableRow key={p.id} className="border-white/5 hover:bg-white/[0.02]">
                  <TableCell><span className="text-sm text-white">{p.brandName} → {p.streamerName}</span></TableCell>
                  <TableCell><span className="text-xs text-gray-400 font-mono">{p.adFilename}</span></TableCell>
                  <TableCell><span className="text-sm text-white font-mono">${p.bidCpm?.toFixed(2)}</span></TableCell>
                  <TableCell><span className="text-sm text-white font-mono">{p.impressions}</span></TableCell>
                  <TableCell><span className="text-sm text-white font-mono">${p.spend?.toFixed(2)}</span></TableCell>
                  <TableCell><StatusBadge status={p.status}/></TableCell>
                </TableRow>
              ))}</TableBody></Table>
            </div>
            )}
          </TabsContent>

          {/* CPM TIERS TAB */}
          <TabsContent value="tiers">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-lg font-bold text-white">CPM Tiers</h3>
              <div className="flex gap-2">
                <Button data-testid="seed-tiers-button" variant="ghost" onClick={seedTiers} className="text-gray-400 hover:text-white border border-white/10 text-xs">Seed Defaults</Button>
                <Button data-testid="add-tier-button" onClick={()=>setShowTierDialog(true)} className="bg-[#FF3B30] hover:bg-[#D70015] text-white text-xs font-bold">+ Add Tier</Button>
              </div>
            </div>
            {cpmTiers.length === 0 ? <div className="text-center py-16 text-gray-500"><BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>No tiers. Click "Seed Defaults" to create standard tiers.</p></div> : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {cpmTiers.map(t => (
                  <div key={t.id} data-testid={`tier-${t.id}`} className="border border-white/10 rounded-lg bg-[#121212]/50 p-5">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-heading text-white font-bold">{t.label}</h4>
                      <Button variant="ghost" size="sm" onClick={()=>deleteTier(t.id)} className="text-gray-500 hover:text-red-400 h-6 w-6 p-0"><XCircle className="w-3.5 h-3.5"/></Button>
                    </div>
                    <p className="text-3xl font-bold text-[#FF3B30] font-mono">${t.floorCpm?.toFixed(2)}</p>
                    <p className="text-xs text-gray-500 font-mono mt-1">CCV: {t.ccvMin.toLocaleString()} - {t.ccvMax.toLocaleString()}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* PAYOUTS TAB */}
          <TabsContent value="payouts">
            <h3 className="font-heading text-lg font-bold text-white mb-4">Payout Requests</h3>
            {payouts.length === 0 ? <div className="text-center py-16 text-gray-500"><Wallet className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>No payout requests</p></div> : (
              <div className="border border-white/10 rounded-lg overflow-hidden">
                <Table><TableHeader><TableRow className="border-white/10 bg-white/[0.02]"><TableHead className="text-gray-500 font-mono text-xs uppercase">Streamer</TableHead><TableHead className="text-gray-500 font-mono text-xs uppercase">Amount</TableHead><TableHead className="text-gray-500 font-mono text-xs uppercase">Date</TableHead><TableHead className="text-gray-500 font-mono text-xs uppercase">Status</TableHead><TableHead className="text-gray-500 font-mono text-xs uppercase">Actions</TableHead></TableRow></TableHeader>
                <TableBody>{payouts.map(p=>(
                  <TableRow key={p.id} className="border-white/5 hover:bg-white/[0.02]">
                    <TableCell><span className="text-sm text-white">{p.streamerName}</span><br/><span className="text-xs text-gray-500 font-mono">{p.streamerEmail}</span></TableCell>
                    <TableCell><span className="text-sm text-white font-mono font-bold">${p.amount?.toFixed(2)}</span></TableCell>
                    <TableCell><span className="text-xs text-gray-400 font-mono">{formatDate(p.requestedAt)}</span></TableCell>
                    <TableCell><StatusBadge status={p.status}/></TableCell>
                    <TableCell>{p.status==='pending'&&<div className="flex gap-1">
                      <Button data-testid={`process-payout-${p.id}`} size="sm" onClick={()=>processPayout(p.id,'process')} className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-7">Process</Button>
                    </div>}{p.status==='processing'&&<Button data-testid={`complete-payout-${p.id}`} size="sm" onClick={()=>processPayout(p.id,'complete')} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7">Complete</Button>}</TableCell>
                  </TableRow>))}</TableBody></Table>
              </div>
            )}
          </TabsContent>

          {/* LIVE CCV TAB */}
          <TabsContent value="ccv">
            <CcvTab API={API} token={token} />
          </TabsContent>

          {/* BILLING APPROVAL TAB */}
          <TabsContent value="billing">
            <BillingTab API={API} token={token} />
          </TabsContent>
        </Tabs>
      </main>

      {/* Review Ad Dialog */}
      <Dialog open={!!reviewAd} onOpenChange={()=>setReviewAd(null)}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-xl">
          <DialogHeader><DialogTitle className="text-white font-heading">Review Ad</DialogTitle><DialogDescription className="text-gray-500">{reviewAd?.filename}</DialogDescription></DialogHeader>
          {reviewAd&&(<div>
            <div className="aspect-video bg-black rounded overflow-hidden mb-4">
              {reviewAd.mimeType?.startsWith('video/') ? <video src={getFullUrl(reviewAd.url)} controls autoPlay className="w-full h-full object-contain"/> :
               reviewAd.filename?.endsWith('.html') || reviewAd.mimeType === 'text/html' ? <iframe src={getFullUrl(reviewAd.url)} title="Holding Preview" className="w-full h-full border-none" sandbox="allow-scripts allow-same-origin" /> :
               <img src={getFullUrl(reviewAd.url)} alt="" className="w-full h-full object-contain"/>}
            </div>
            <div className="flex items-center gap-2 mb-4">
              <Badge className={`text-[9px] font-mono ${reviewAd.adType === 'holding' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : reviewAd.adType === 'overlay' ? 'bg-[#7C3AED]/10 text-[#7C3AED] border-[#7C3AED]/20' : 'bg-white/5 text-gray-400 border-white/10'}`}>
                {reviewAd.adType === 'holding' ? 'Holding Screen' : reviewAd.adType === 'overlay' ? 'Overlay' : `${reviewAd.slotDuration || 15}s Fullscreen`}
              </Badge>
              {reviewAd.adType === 'holding' && <span className="text-[9px] text-gray-500 font-mono">HTML/Image — renders as BRB screen between ad breaks</span>}
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs font-mono text-gray-400 mb-4"><div>Brand: <span className="text-white">{reviewAd.creatorName}</span></div><div>Size: <span className="text-white">{formatBytes(reviewAd.fileSize)}</span></div></div>
            {reviewAd.qrShortCode && (
              <div data-testid="admin-qr-check" className={`rounded border px-3 py-2.5 mb-4 ${
                reviewAd.qrStatus === 'verified' ? 'border-emerald-500/20 bg-emerald-500/5' :
                reviewAd.qrStatus === 'flagged' ? 'border-red-500/20 bg-red-500/5' : 'border-yellow-500/20 bg-yellow-500/5'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <QrCode className="w-3.5 h-3.5 text-gray-300" />
                  <span className="text-[10px] font-mono uppercase tracking-widest text-gray-300">QR destination · auto-check: {reviewAd.qrStatus}</span>
                  {reviewAd.qrCheck?.confidence ? <span className="text-[10px] font-mono text-gray-500">{reviewAd.qrCheck.confidence}%</span> : null}
                </div>
                <a href={reviewAd.qrUrl} target="_blank" rel="noreferrer" className="text-[11px] font-mono text-blue-400 hover:underline break-all">{reviewAd.qrUrl}</a>
                {reviewAd.qrCheck?.reason && <p className="text-[11px] text-gray-400 mt-1">{reviewAd.qrCheck.reason}</p>}
              </div>
            )}
            <Textarea data-testid="review-notes-input" placeholder="Notes (required for rejection)..." value={reviewNotes} onChange={e=>setReviewNotes(e.target.value)} className="bg-black/40 border-white/10 text-white placeholder:text-gray-600 min-h-[80px] mb-4"/>
            <DialogFooter className="gap-3"><Button data-testid="reject-ad-button" variant="ghost" className="flex-1 text-red-400 hover:text-red-300 hover:bg-red-500/10 border border-red-500/20" onClick={()=>rejectAd(reviewAd.id)}><XCircle className="w-4 h-4 mr-1"/>Reject</Button><Button data-testid="approve-ad-button" className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold" onClick={()=>approveAd(reviewAd.id)}><CheckCircle className="w-4 h-4 mr-1"/>Approve</Button></DialogFooter>
          </div>)}
        </DialogContent>
      </Dialog>

      {/* Review Placement Dialog */}
      <Dialog open={!!reviewPlacement} onOpenChange={()=>setReviewPlacement(null)}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-md">
          <DialogHeader><DialogTitle className="text-white font-heading">Review Placement</DialogTitle><DialogDescription className="text-gray-500">{reviewPlacement?.brandName} → {reviewPlacement?.streamerName}</DialogDescription></DialogHeader>
          {reviewPlacement&&(<div>
            <div className="grid grid-cols-2 gap-3 text-sm font-mono mb-4">
              <div className="p-3 bg-white/[0.03] rounded"><p className="text-gray-500 text-xs">Bid CPM</p><p className="text-white font-bold">${reviewPlacement.bidCpm?.toFixed(2)}</p></div>
              <div className="p-3 bg-white/[0.03] rounded"><p className="text-gray-500 text-xs">Floor CPM</p><p className="text-white font-bold">${reviewPlacement.floorCpm?.toFixed(2)}</p></div>
            </div>
            <p className="text-xs text-gray-400 font-mono mb-4">Ad: {reviewPlacement.adFilename}</p>
            <Textarea data-testid="placement-review-notes" placeholder="Notes (required for rejection)..." value={reviewNotes} onChange={e=>setReviewNotes(e.target.value)} className="bg-black/40 border-white/10 text-white placeholder:text-gray-600 min-h-[60px] mb-4"/>
            <DialogFooter className="gap-3"><Button data-testid="reject-placement-button" variant="ghost" className="flex-1 text-red-400 hover:text-red-300 hover:bg-red-500/10 border border-red-500/20" onClick={()=>rejectPlacement(reviewPlacement.id)}><XCircle className="w-4 h-4 mr-1"/>Reject</Button><Button data-testid="approve-placement-button" className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold" onClick={()=>approvePlacement(reviewPlacement.id)}><CheckCircle className="w-4 h-4 mr-1"/>Approve</Button></DialogFooter>
          </div>)}
        </DialogContent>
      </Dialog>

      {/* Add Tier Dialog */}
      <Dialog open={showTierDialog} onOpenChange={setShowTierDialog}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-sm">
          <DialogHeader><DialogTitle className="text-white font-heading">Add CPM Tier</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-mono text-gray-500 uppercase">Label</Label><Input value={tierForm.label} onChange={e=>setTierForm(f=>({...f,label:e.target.value}))} className="bg-black/40 border-white/10 text-white mt-1" placeholder="Premium"/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-mono text-gray-500 uppercase">Min CCV</Label><Input type="number" value={tierForm.ccvMin} onChange={e=>setTierForm(f=>({...f,ccvMin:e.target.value}))} className="bg-black/40 border-white/10 text-white mt-1"/></div>
              <div><Label className="text-xs font-mono text-gray-500 uppercase">Max CCV</Label><Input type="number" value={tierForm.ccvMax} onChange={e=>setTierForm(f=>({...f,ccvMax:e.target.value}))} className="bg-black/40 border-white/10 text-white mt-1"/></div>
            </div>
            <div><Label className="text-xs font-mono text-gray-500 uppercase">Floor CPM ($)</Label><Input type="number" step="0.01" value={tierForm.floorCpm} onChange={e=>setTierForm(f=>({...f,floorCpm:e.target.value}))} className="bg-black/40 border-white/10 text-white mt-1"/></div>
          </div>
          <DialogFooter><Button onClick={createTier} className="bg-[#FF3B30] hover:bg-[#D70015] text-white font-bold w-full">Create Tier</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function CcvTab({ API, token }) {
  const [liveCreators, setLiveCreators] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [polling, setPolling] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchLive = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/ccv/live-status`, { headers });
      setLiveCreators(r.data || []);
    } catch {}
  }, [API, token]);

  useEffect(() => { fetchLive(); }, [fetchLive]);

  const pollAll = async () => {
    setPolling(true);
    try {
      const r = await axios.post(`${API}/ccv/poll-all`, {}, { headers });
      toast.success(`Polled ${r.data.polled} reading(s)`);
      fetchLive();
    } catch (e) { toast.error(e.response?.data?.detail || 'Poll failed'); }
    setPolling(false);
  };

  const viewComparison = async (streamerId) => {
    try {
      const r = await axios.get(`${API}/ccv/comparison/${streamerId}`, { headers });
      setComparison(r.data);
    } catch (e) { toast.error('Failed to load comparison'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-lg font-bold text-white uppercase">Live Viewer Counts</h3>
          <p className="text-xs text-gray-500 font-mono">Verified CCV vs self-reported — tracked every 60 seconds</p>
        </div>
        <Button data-testid="poll-all-ccv" onClick={pollAll} disabled={polling} className="bg-emerald-600 hover:bg-emerald-700 text-white font-mono text-xs">
          <Signal className="w-3.5 h-3.5 mr-1.5" /> {polling ? 'Polling...' : 'Poll All Creators'}
        </Button>
      </div>

      {/* Live Creators Table */}
      <div className="border border-white/10 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10">
              <TableHead className="text-gray-500 font-mono text-xs uppercase">Creator</TableHead>
              <TableHead className="text-gray-500 font-mono text-xs uppercase">Status</TableHead>
              <TableHead className="text-gray-500 font-mono text-xs uppercase text-right">Self-Reported</TableHead>
              <TableHead className="text-gray-500 font-mono text-xs uppercase text-right">Live / Verified</TableHead>
              <TableHead className="text-gray-500 font-mono text-xs uppercase text-right">Last Check</TableHead>
              <TableHead className="text-gray-500 font-mono text-xs uppercase text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {liveCreators.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-gray-600 font-mono text-xs py-8">No creators have linked platforms yet</TableCell></TableRow>
            ) : liveCreators.map(c => (
              <TableRow key={c.userId} className="border-white/5 hover:bg-white/[0.02]">
                <TableCell className="font-mono text-sm text-white">{c.displayName || c.userName || 'Unknown'}</TableCell>
                <TableCell>
                  {c.isLive ? (
                    <span className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                      <span className="text-xs font-mono font-bold text-emerald-400 uppercase">Live</span>
                    </span>
                  ) : (
                    <span className="text-xs font-mono text-gray-600 uppercase">Offline</span>
                  )}
                </TableCell>
                <TableCell className="text-right font-mono text-sm text-white">{(c.avgCCV || 0).toLocaleString()}</TableCell>
                <TableCell className="text-right">
                  <span className={`font-mono text-sm font-bold ${c.liveCcv ? 'text-emerald-400' : 'text-gray-600'}`}>
                    {c.liveCcv ? c.liveCcv.toLocaleString() : '---'}
                  </span>
                </TableCell>
                <TableCell className="text-right text-xs font-mono text-gray-500">
                  {c.lastCcvCheck ? new Date(c.lastCcvCheck).toLocaleTimeString() : '---'}
                </TableCell>
                <TableCell className="text-center">
                  <Button size="sm" variant="ghost" onClick={() => viewComparison(c.userId)} className="text-gray-500 hover:text-white text-[10px] font-mono uppercase h-7">
                    <Eye className="w-3 h-3 mr-1" /> Compare
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Comparison Dialog */}
      {comparison && (
        <div className="border border-white/10 rounded-lg bg-[#121212] p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-heading text-sm font-bold text-white uppercase">CCV Comparison — {comparison.streamerName}</h4>
            <button onClick={() => setComparison(null)} className="text-xs font-mono text-gray-500 hover:text-white">Close</button>
          </div>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="p-4 bg-white/[0.03] rounded border border-white/5 text-center">
              <p className="text-[9px] font-mono text-gray-500 uppercase mb-1">Self-Reported Avg</p>
              <p className="text-2xl font-bold text-white font-mono">{(comparison.selfReported.avgCCV || 0).toLocaleString()}</p>
            </div>
            <div className="p-4 bg-white/[0.03] rounded border border-emerald-500/20 text-center">
              <p className="text-[9px] font-mono text-emerald-400 uppercase mb-1">Verified Avg (7d)</p>
              <p className="text-2xl font-bold text-emerald-400 font-mono">{comparison.verified.avgCCV ? comparison.verified.avgCCV.toLocaleString() : '---'}</p>
              <p className="text-[9px] font-mono text-gray-600 mt-1">{comparison.verified.readingCount} readings</p>
            </div>
            <div className="p-4 bg-white/[0.03] rounded border border-white/5 text-center">
              <p className="text-[9px] font-mono text-gray-500 uppercase mb-1">Discrepancy</p>
              {comparison.discrepancy !== null ? (
                <p className={`text-2xl font-bold font-mono ${comparison.discrepancy > comparison.selfReported.avgCCV * 0.2 ? 'text-red-400' : 'text-emerald-400'}`}>
                  {comparison.discrepancy > 0 ? `${comparison.discrepancy.toLocaleString()}` : '0'}
                </p>
              ) : (
                <p className="text-2xl font-bold text-gray-600 font-mono">---</p>
              )}
            </div>
          </div>
          {comparison.verified.platforms?.length > 0 && (
            <p className="text-[10px] font-mono text-gray-500">Verified on: {comparison.verified.platforms.join(', ')}</p>
          )}
          {comparison.liveCcv && (
            <p className="text-[10px] font-mono text-emerald-400 mt-1">Currently live: {comparison.liveCcv.toLocaleString()} CCV</p>
          )}
        </div>
      )}
    </div>
  );
}


function BillingTab({ API, token }) {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchPending = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/billing/pending`, { headers });
      setPending(r.data || []);
    } catch {}
  }, [API, token]);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const approve = async (id) => {
    try {
      await axios.post(`${API}/admin/billing/${id}/approve`, {}, { headers });
      toast.success('Billing approved');
      fetchPending();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const reject = async (id) => {
    try {
      await axios.post(`${API}/admin/billing/${id}/reject`, {}, { headers });
      toast.success('Billing rejected & charge reversed');
      fetchPending();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const approveAll = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/admin/billing/approve-all`, {}, { headers });
      toast.success(r.data.message);
      fetchPending();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-lg font-bold text-white uppercase">Billing Approval</h3>
          <p className="text-xs text-gray-500 font-mono">Earnings using self-reported CCV need admin approval before payout</p>
        </div>
        {pending.length > 0 && (
          <Button data-testid="approve-all-billing" onClick={approveAll} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 text-white font-mono text-xs">
            <CheckCircle className="w-3.5 h-3.5 mr-1.5" /> Approve All ({pending.length})
          </Button>
        )}
      </div>

      {pending.length === 0 ? (
        <div className="text-center py-12 border border-white/10 rounded-lg">
          <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
          <p className="text-sm text-gray-400 font-mono">All earnings verified — nothing pending</p>
        </div>
      ) : (
        <div className="border border-white/10 rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10">
                <TableHead className="text-gray-500 font-mono text-xs uppercase">Date</TableHead>
                <TableHead className="text-gray-500 font-mono text-xs uppercase">Streamer</TableHead>
                <TableHead className="text-gray-500 font-mono text-xs uppercase">Ad</TableHead>
                <TableHead className="text-gray-500 font-mono text-xs uppercase">CCV Source</TableHead>
                <TableHead className="text-gray-500 font-mono text-xs uppercase text-right">CCV</TableHead>
                <TableHead className="text-gray-500 font-mono text-xs uppercase text-right">Charge</TableHead>
                <TableHead className="text-gray-500 font-mono text-xs uppercase text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pending.map(e => (
                <TableRow key={e.id} className="border-white/5 hover:bg-white/[0.02]">
                  <TableCell className="text-xs font-mono text-gray-400">{e.createdAt ? new Date(e.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</TableCell>
                  <TableCell className="text-sm text-white font-mono">{e.streamerName}</TableCell>
                  <TableCell className="text-xs text-gray-400 font-mono truncate max-w-[150px]">{e.adFilename}</TableCell>
                  <TableCell>
                    <span className={`text-[10px] font-mono font-bold uppercase px-1.5 py-0.5 rounded ${e.ccvSource === 'live' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`}>
                      {e.ccvSource || 'self_reported'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm text-white">{(e.billingCcv || 0).toLocaleString()}</TableCell>
                  <TableCell className="text-right font-mono text-sm text-emerald-400">${(e.grossEarning || 0).toFixed(4)}</TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => approve(e.id)} className="text-emerald-400 hover:bg-emerald-500/10 h-7 px-2 text-[10px] font-mono">
                        <CheckCircle className="w-3 h-3 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => reject(e.id)} className="text-red-400 hover:bg-red-500/10 h-7 px-2 text-[10px] font-mono">
                        <XCircle className="w-3 h-3 mr-1" /> Reject
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
